import { forwardRef, useEffect, useRef, useState } from 'react'
import { isValid, parse } from 'date-fns'
import { SimpleCalendarIcon } from '../icons.jsx'
import { useTranslation } from 'react-i18next'

// Everything needed to replace bootstrap datepicker native html masked input with a custom implementation
// that supports numeric-only input, auto-formatting, and validation on blur, based on the user's locale format. 
// The DateFieldInput component is used as the custom input for react-datepicker in DefaultDatePicker.jsx.

/** Inserts separators into a digit string using block lengths (e.g., "24122025" → "24.12.2025") */
export const applyMask = (digits, blocks, separator, isBackspacing = false) => {
  const parts = []
  let index = 0
  let endedOnBoundary = false

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i]
    if (index >= digits.length) break
    const part = digits.slice(index, index + block)
    parts.push(part)
    index += block

    // Append trailing separator at segment boundary unless backspacing (e.g. "24" → "24.")
    endedOnBoundary = part.length === block && i < blocks.length - 1 && index === digits.length && !isBackspacing
  }

  const masked = parts.join(separator)
  return endedOnBoundary ? `${masked}${separator}` : masked
}

/** Maps a digit-index position to its corresponding cursor position in the masked string */
export const caretFromDigitIndex = (masked, digitIndex) => {
  if (digitIndex <= 0) return 0

  let seenDigits = 0
  for (let i = 0; i < masked.length; i++) {
    if (/\d/.test(masked[i])) {
      seenDigits++
      if (seenDigits >= digitIndex) return i + 1
    }
  }

  return masked.length
}

/**
 * Masked date input with floating label, auto-formatting, and blur validation.
 * @param {Object} props.localeConfig - { format, blocks, separator, placeholder }
 */
const DateFieldInput = forwardRef(
  ({ value, onClick, id, name, label, required, disabled, error: externalError, onDateChange, localeConfig }, ref) => {

    const inputRef = useRef(null)
    const deleteIntentRef = useRef(false)
    const [editingText, setEditingText] = useState(null)
    const [typeError, setTypeError] = useState(false)
    const { t } = useTranslation()

    useEffect(() => {
      setEditingText(null)
      setTypeError(false)
    }, [value])

    const totalDigits = localeConfig.blocks.reduce((sum, v) => sum + v, 0)
    const displayValue = editingText !== null ? editingText : (value ?? '')
    const isErrored = typeError || !!externalError

    const setRef = (node) => {
      inputRef.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    }

    const handleChange = (e) => {
      const rawValue = e.target.value
      const rawCaret = e.target.selectionStart ?? rawValue.length
      const digitsBeforeCaret = rawValue.slice(0, rawCaret).replace(/\D/g, '').length

      // User types only numbers; separators are visualized automatically based on the locale format and disappear on backspace.
      const digits = rawValue.replace(/\D/g, '').slice(0, totalDigits)
      const prevDigitCount = (displayValue ?? '').replace(/\D/g, '').length
      const currDigitCount = digits.length
      const isBackspacing = prevDigitCount > currDigitCount || deleteIntentRef.current
      deleteIntentRef.current = false

      const masked = applyMask(digits, localeConfig.blocks, localeConfig.separator, isBackspacing)

      setEditingText(masked)
      setTypeError(false)

      let nextCaret = caretFromDigitIndex(masked, digitsBeforeCaret)
      if (nextCaret < masked.length && masked[nextCaret] === localeConfig.separator) {
        nextCaret++
      }

      setTimeout(() => {
        inputRef.current?.setSelectionRange(nextCaret, nextCaret)
      }, 0)
    }

    const handleKeyDown = (e) => {
      deleteIntentRef.current = e.key === 'Backspace' || e.key === 'Delete'
      if (e.key === 'Enter') e.currentTarget.blur()
    }

    const handleBlur = () => {
      if (editingText === null) return
      if (editingText.trim() === '') {
        onDateChange?.(null)
        return
      }

      const parsed = parse(editingText, localeConfig.format, new Date())
      if (isValid(parsed)) {
        setTypeError(false)
        setEditingText(null)
        onDateChange?.(parsed)
      } else {
        setTypeError(true)
      }
    }

    return (
      <div className='dp-field-container'>
        {label && (
          <label
            htmlFor={id}
            className={[
              'dp-field-label',
              required ? 'dp-field-label--required' : '',
              isErrored ? 'dp-field-label--error' : ''
            ]
              .filter(Boolean)
              .join(' ')}
            title={label}
          >
            {label}
          </label>
        )}
        <div
          className={[
            'dp-date-field',
            'dp-date-field--single',
            disabled ? 'dp-date-field--disabled' : '',
            isErrored ? 'dp-date-field--error' : ''
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div className={isErrored ? 'dp-input-wrapper dp-input-wrapper--error' : 'dp-input-wrapper'}>
            <input
              id={id}
              name={name}
              data-testid={`${id ?? name}-datepicker-input`}
              ref={setRef}
              value={displayValue}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              placeholder={localeConfig.placeholder}
              disabled={disabled}
              aria-invalid={isErrored}
              aria-required={required}
              className='dp-single-input'
              type='text'
              inputMode='numeric'
            />
            <span
              className='dp-calendar-icon'
              data-testid={`${id ?? name}-datepicker-icon`}
              onClick={disabled ? undefined : onClick}
              role='button'
              tabIndex={-1}
              aria-hidden='true'
            >
              <SimpleCalendarIcon />
            </span>
          </div>
        </div>
        {isErrored && <small className='dp-error-text'>{externalError || t('InvalidDateFormat')}</small>}
      </div>
    )
  }
)

DateFieldInput.displayName = 'DateFieldInput'

export default DateFieldInput
